(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exportFn(
            require('../modules/data/channels.js'),
            {
                facebookAds: require('./facebookAds.js')
            },
            require('../modules/formatter.js'),
            require('../modules/sortFunctions.js'),
            require('../enums/colors.js'),
            require('../enums/likelyPromoted.js'),
            require('../enums/metrics.js'),
            require('../strings/strings.js'),
            require('../str.js'),
            require('../modules/utils/metric.js'),
            require('lodash')
        );

    } else {
        RivalIQ.metrics.definitions.facebook = exportFn(
            RivalIQ.data.channels,
            RivalIQ.metrics.definitions,
            RivalIQ.formatter,
            RivalIQ.sortFunctions,
            RivalIQ.enums.colors,
            RivalIQ.enums.likelyPromoted,
            RivalIQ.enums,
            RivalIQ.strings,
            RivalIQ.str,
            RivalIQ.utils.metric,
            window._
        );
    }

    function exportFn(channels, definitions, formatter, sortFunctions, colors, likelyPromoted, metricEnums, strings, str, utils, _) {
        const channel = channels.get('facebook');

        function valueByDimension(dimension, a, b) {
            return function (val, metric) {
                if (_.includes(metric.dimensions, dimension)) {
                    return a;
                }
                return b;
            };
        }

        // CMPTE-403: Meta deprecated several FB Page Insights metrics on June 17, 2026 with no
        // recovery path (the `is_from_ads` breakdown yields only paid/organic, not viral/nonviral).
        // The picker is gated via removal from public/modules/data/metrics.js, and saved widgets
        // referencing these metrics surface the reason via the existing [Deprecated] picker tooltip
        // path and the enriched description below. See docs/adr/0001 and docs/adr/0002.
        const DEPRECATED_AFTER_JUNE_2026_REASON = 'No longer reported by Facebook as of June 17, 2026.';
        const DEPRECATED_AFTER_JUNE_2026_DESCRIPTION_NOTE = 'As of June 17, 2026, due to Facebook deprecations, this metric is no longer reported by Facebook. Values may be unavailable for date ranges starting after that date.';

        function withDeprecatedAfterJune2026Note(description) {
            if (_.isFunction(description)) {
                return function () {
                    const base = description.apply(null, arguments);
                    return base ?
                        `${base}<br/><br/>${DEPRECATED_AFTER_JUNE_2026_DESCRIPTION_NOTE}` :
                        DEPRECATED_AFTER_JUNE_2026_DESCRIPTION_NOTE;
                };
            }
            return description ?
                `${description}<br/><br/>${DEPRECATED_AFTER_JUNE_2026_DESCRIPTION_NOTE}` :
                DEPRECATED_AFTER_JUNE_2026_DESCRIPTION_NOTE;
        }

        return {
            className: channel.get('channel'),
            format: channel.get('name'),
            name: channel.get('name'),
            color: colors.facebookHighContrast,
            published_at: {
                collection: metricEnums.collection.GENERAL
            },
            media_type: {
                name: 'Post Type',
                collection: metricEnums.collection.GENERAL,
                type: metricEnums.dataType.STRING,
                description: strings.facebook_insights_post_media_type_description
            },
            views: {
                name: 'Video Views',
                category: 'post_analysis',
                description: valueByDimension('post', strings.facebook_post_video_views_description, strings.facebook_video_views_description),
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            post_views: {
                name: 'Video Post Views',
                category: 'post_analysis',
                description: valueByDimension('post', strings.facebook_post_video_post_views_description, strings.facebook_video_post_views_description),
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            engagement_rate: {
                name: 'Engagement Rate by Page Fan',
                description: strings.facebook_posts_engagement_rate_description,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            avg_engagement_rate_per_post: {
                name: 'Engagement Rate by Page Fan',
                description: strings.facebook_average_post_engagement_rate_description
            },
            avg_engagement_rate_per_non_boosted_post: {
                description: strings.facebook_average_non_boosted_post_engagement_rate_description
            },
            avg_engagement_rate_per_likely_boosted_post: {
                description: strings.facebook_average_likely_boosted_post_engagement_rate_description
            },
            avg_count_per_day: {
                description: strings.facebook_average_daily_posts_description
            },
            avg_count_per_week: {
                description: strings.facebook_avg_count_per_week_description
            },
            audience: {
                name: 'Page Fans',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_presence_reach_description;
                        }
                    }
                    return strings.facebook_page_fans_description;
                },
                metricType: metricEnums.metricType.TOTAL,
                category: 'post_analysis',
                collection: metricEnums.collection.AUDIENCE,
                noHistoricData: true
            },
            presence_reach: {
                name: 'Page Fans',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_presence_reach_description;
                        }
                    }
                    return strings.facebook_page_fans_description;
                },
                noHistoricData: true,
                collection: valueByDimension('post', metricEnums.collection.GENERAL, metricEnums.collection.AUDIENCE),
                summable: false
            },
            all_presence_reach: {
                name: 'Page Fans, Global',
                description: strings.facebook_all_presence_reach_desc,
                summable: false
            },
            potential_reach: {
                description: strings.facebook_potential_reach_description,
                summable: false
            },
            followers_count: {
                name: 'Page Followers',
                description: strings.facebook_followers_count_description,
                metricType: metricEnums.metricType.TOTAL,
                collection: metricEnums.collection.AUDIENCE,
                noHistoricData: true
            },
            talking_about: {
                name: 'Talking About',
                description: strings.facebook_talking_about_description,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                metricValueType: metricEnums.metricValueType.AVERAGE
            },
            avg_engagement_total_per_day: {
                description: strings.facebook_average_daily_posts_engagement_total_description
            },
            engagement_type: {
                // This defines the order these will appear in bar charts.
                dimensionValues: _.constant(['applause', 'conversation', 'amplification']),

                value: {
                    applause: {
                        format: utils.formatDimensionFn('Reactions'),
                        className: 'facebook',
                        description: strings.facebook_posts_reactions_description,

                        engagement_total: {
                            name: 'Reactions',
                            description: valueByDimension('post', strings.facebook_post_reactions_description, strings.facebook_posts_reactions_description)
                        },

                        avg_engagement_total_per_day: {
                            name: 'Reactions / Day',
                            description: strings.facebook_average_daily_posts_reactions_description
                        },

                        reaction_type: {
                            value: {
                                likes: {
                                    format: 'Likes',
                                    className: 'facebook',
                                    engagement_total: {
                                        name: 'Likes'
                                    },
                                    avg_engagement_total_per_day: {
                                        name: 'Likes / Day'
                                    }
                                },
                                loves: {
                                    format: 'Loves',
                                    className: 'x-facebook-secondary',
                                    engagement_total: {
                                        name: 'Loves',
                                        description: strings.facebook_posts_loves_description
                                    },
                                    avg_engagement_total_per_day: {
                                        name: 'Loves / Day',
                                        description: strings.facebook_average_daily_posts_loves_description
                                    }
                                },
                                hahas: {
                                    format: 'Hahas',
                                    className: 'x-facebook-tertiary',
                                    engagement_total: {
                                        name: 'Hahas',
                                        description: strings.facebook_posts_hahas_description
                                    },
                                    avg_engagement_total_per_day: {
                                        name: 'Hahas / Day',
                                        description: strings.facebook_average_daily_posts_hahas_description
                                    }
                                },
                                wows: {
                                    format: 'Wows',
                                    className: 'x-facebook-fourth',
                                    engagement_total: {
                                        name: 'Wows',
                                        description: strings.facebook_posts_wows_description
                                    },
                                    avg_engagement_total_per_day: {
                                        name: 'Wows / Day',
                                        description: strings.facebook_average_daily_posts_wows_description
                                    }
                                },
                                sads: {
                                    format: 'Sads',
                                    className: 'x-facebook-fifth',
                                    engagement_total: {
                                        name: 'Sads',
                                        description: strings.facebook_posts_sads_description
                                    },
                                    avg_engagement_total_per_day: {
                                        name: 'Sads / Day',
                                        description: strings.facebook_average_daily_posts_sads_description
                                    }
                                },
                                angrys: {
                                    format: 'Angrys',
                                    className: 'x-facebook-sixth',
                                    engagement_total: {
                                        name: 'Angrys',
                                        description: strings.facebook_posts_angrys_description
                                    },
                                    avg_engagement_total_per_day: {
                                        name: 'Angrys / Day',
                                        description: strings.facebook_average_daily_posts_angrys_description
                                    }
                                }
                            }
                        }
                    },
                    amplification: {
                        format: utils.formatDimensionFn('Shares'),
                        className: 'x-facebook-secondary',
                        engagement_total: {
                            name: 'Shares',
                            description: valueByDimension('post', strings.facebook_post_shares_description, strings.facebook_posts_shares_description)
                        },

                        avg_engagement_total_per_day: {
                            name: 'Shares / Day',
                            description: strings.facebook_average_daily_posts_shares_description
                        }
                    },
                    conversation: {
                        format: utils.formatDimensionFn('Comments'),
                        className: 'x-facebook-tertiary',
                        engagement_total: {
                            name: 'Comments',
                            description: valueByDimension('post', strings.facebook_post_comments_description, strings.facebook_posts_comments_description)
                        },

                        avg_engagement_total_per_day: {
                            name: 'Comments / Day',
                            description: strings.facebook_average_daily_posts_comments_description
                        }
                    }
                }
            },
            count_likely_boosted: {
                name: 'Likely Boosted Posts',
                description: strings.facebook_posts_likely_boosted_description
            },
            count_non_boosted: {
                name: 'Likely Organic Posts',
                description: strings.facebook_posts_non_boosted_description
            },
            gender: {
                page_fans: {
                    collection: metricEnums.collection.AUDIENCE
                },
                unique_impressions: {
                    collection: metricEnums.collection.REACH,
                    summable: false
                }
            },
            unique_impressions: {
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMERIC,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                name: function (val, metric) {
                    const gender = metric.gender;
                    if (gender) {
                        const age = metric.age_range;
                        const names = utils.genderAgeNames(gender, age);

                        if (age) {
                            return ['Presence Reach / Day, ', names.gender, names.age].join(' ');
                        }

                        return `Presence Reach / Day, ${names.gender}`;
                    }

                    return 'Presence Reach / Day';
                },
                description: function (val, metric) {
                    const gender = metric.gender;
                    if (gender) {
                        const age = metric.age_range;
                        const names = utils.genderAgeNames(gender, age);

                        if (age) {
                            return str('facebook_impressions_gender_age_description', names.title, age);
                        }

                        return str('facebook_impressions_gender_description', names.title);
                    }

                    return strings.facebook_insights_impressions_unique_description;
                },
                collection: metricEnums.collection.REACH,
                summable: false
            },
            consumptions: {
                name: 'Presence Consumptions',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_consumptions_description,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            unique_consumptions: {
                name: 'Presence Consumptions / Day, Unique',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_consumptions_unique_description,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            page_engagement_rate: {
                name: 'Presence Consumption Rate by Impression',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_page_engagement_rate_description,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT
            },
            page_fans: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                metricType: metricEnums.metricType.TOTAL,
                name: function (val, metric) {
                    const gender = metric.gender;
                    if (gender) {
                        const age = metric.age_range;
                        const names = utils.genderAgeNames(gender, age);

                        if (age) {
                            return ['Page Fans, ', names.gender, names.age].join(' ');
                        }

                        return `Page Fans, ${names.gender}`;
                    }

                    return 'Page Fans';
                },
                description: function (val, metric) {
                    const gender = metric.gender;
                    if (gender) {
                        const age = metric.age_range;
                        const names = utils.genderAgeNames(gender, age);

                        if (age) {
                            return str('facebook_fans_gender_age_description', names.title, age);
                        }

                        return str('facebook_fans_gender_description', names.title);
                    }

                    return strings.facebook_insights_page_fans_description;
                },
                collection: metricEnums.collection.AUDIENCE
            },
            page_fan_adds: {
                name: 'Fan Adds',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_page_fan_adds_description,
                metricType: metricEnums.metricType.VOLUME,
                collection: metricEnums.collection.AUDIENCE
            },
            unique_page_fan_adds: {
                name: 'Fan Adds / Day, Unique',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_page_fan_adds_unique_description,
                metricType: metricEnums.metricType.VOLUME,
                collection: metricEnums.collection.AUDIENCE,
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                summable: false
            },
            page_fan_removes: {
                name: 'Fan Removes',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_page_fan_removes_description,
                metricType: metricEnums.metricType.VOLUME,
                collection: metricEnums.collection.AUDIENCE
            },
            unique_page_fan_removes: {
                name: 'Fan Removes / Day, Unique',
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_page_fan_removes_unique_description,
                metricType: metricEnums.metricType.VOLUME,
                collection: metricEnums.collection.AUDIENCE,
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                summable: false
            },
            consumption_type: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                value: {
                    photo_view: {
                        consumptions: {
                            name: 'Presence Photo View Consumptions',
                            description: strings.facebook_insights_consumptions_photo_views_description
                        },
                        unique_consumptions: {
                            name: 'Presence Photo View Consumptions / Day, Unique',
                            description: strings.facebook_insights_consumptions_photo_views_unique_description
                        }
                    },
                    video_play: {
                        consumptions: {
                            name: 'Presence Video Play Consumptions',
                            description: strings.facebook_insights_consumptions_video_play_description
                        },
                        unique_consumptions: {
                            name: 'Presence Video Play Consumptions / Day, Unique',
                            description: strings.facebook_insights_consumptions_video_play_unique_description
                        }
                    },
                    link_clicks: {
                        consumptions: {
                            name: 'Presence Link Click Consumptions',
                            description: strings.facebook_insights_consumptions_link_clicks_description
                        },
                        unique_consumptions: {
                            name: 'Presence Link Consumptions / Day, Unique',
                            description: strings.facebook_insights_consumptions_link_clicks_unique_description
                        }
                    },
                    other_clicks: {
                        consumptions: {
                            name: 'Presence Other Click Consumptions',
                            description: strings.facebook_insights_consumptions_other_clicks_description
                        },
                        unique_consumptions: {
                            name: 'Presence Other Click Consumptions / Day, Unique',
                            description: strings.facebook_insights_consumptions_other_clicks_unique_description
                        }
                    }
                }
            },
            impression_type: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                value: {
                    paid: {
                        impressions: {
                            name: 'Presence Impressions, Paid',
                            description: strings.facebook_insights_impressions_paid_description
                        },
                        unique_impressions: {
                            name: 'Presence Reach / Day, Paid',
                            description: strings.facebook_insights_impressions_paid_unique_description
                        },
                        impressions_per_day: {
                            name: 'Presence Impressions / Day, Paid',
                            description: strings.facebook_insights_presence_impressions_per_day_paid_description,
                            source: metricEnums.sources.FACEBOOK_INSIGHTS,
                            collection: metricEnums.collection.IMPRESSIONS,
                            type: metricEnums.dataType.NUMERIC
                        }
                    },
                    organic: {
                        impressions: {
                            name: 'Presence Impressions, Organic',
                            description: strings.facebook_insights_impressions_organic_description
                        },
                        unique_impressions: {
                            name: 'Presence Reach / Day, Organic',
                            description: strings.facebook_insights_impressions_organic_unique_description
                        },
                        impressions_per_day: {
                            name: 'Presence Impressions / Day, Organic',
                            description: strings.facebook_insights_presence_impressions_per_day_organic_description,
                            source: metricEnums.sources.FACEBOOK_INSIGHTS,
                            collection: metricEnums.collection.IMPRESSIONS,
                            type: metricEnums.dataType.NUMERIC
                        }
                    },
                    viral: {
                        impressions: {
                            name: 'Presence Impressions, Organic, Viral',
                            description: strings.facebook_insights_impressions_viral_description
                        },
                        unique_impressions: {
                            name: 'Presence Reach / Day, Organic, Viral',
                            description: strings.facebook_insights_impressions_viral_unique_description
                        },
                        impressions_per_day: {
                            name: 'Presence Impressions / Day, Organic, Viral',
                            description: strings.facebook_insights_presence_impressions_per_day_viral_description,
                            source: metricEnums.sources.FACEBOOK_INSIGHTS,
                            collection: metricEnums.collection.IMPRESSIONS,
                            type: metricEnums.dataType.NUMERIC
                        }
                    },
                    nonviral: {
                        impressions: {
                            name: 'Presence Impressions, Organic, Non-Viral',
                            description: strings.facebook_insights_impressions_nonviral_description
                        },
                        unique_impressions: {
                            name: 'Presence Reach / Day, Organic, Non-Viral',
                            description: strings.facebook_insights_impressions_nonviral_unique_description
                        },
                        impressions_per_day: {
                            name: 'Presence Impressions / Day, Organic, Non-Viral',
                            description: strings.facebook_insights_presence_impressions_per_day_nonviral_description,
                            source: metricEnums.sources.FACEBOOK_INSIGHTS,
                            collection: metricEnums.collection.IMPRESSIONS,
                            type: metricEnums.dataType.NUMERIC
                        }
                    }
                }
            },
            pay_type: {
                value: {
                    unpaid: {
                        page_fan_adds: {
                            name: 'Fan Adds / Day, Organic, Unique',
                            metricValueType: metricEnums.metricValueType.AVERAGE,
                            description: strings.facebook_insights_page_fan_adds_unpaid_description,
                            type: metricEnums.dataType.NUMERIC
                        }
                    },
                    paid: {
                        page_fan_adds: {
                            name: 'Fan Adds / Day, Paid, Unique',
                            metricValueType: metricEnums.metricValueType.AVERAGE,
                            description: strings.facebook_insights_page_fan_adds_paid_description,
                            type: metricEnums.dataType.NUMERIC
                        }
                    }
                }
            },
            promoted_likelyhood: {
                name: 'Likely Boosted',
                requiredPlanFeature: 'has_likely_promoted',
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                description: strings.promoted_likelyhood_description,
                missingFormat: `<i class="fa fa-fw fa-minus-circle"></i>${strings.likely_promoted_unknown}`,
                format: function (data) {
                    const likelyhood = (_.isArray(data) ? _.last(data) : data);
                    if (likelyhood >= likelyPromoted.threshold) {
                        return `<i class="fa fa-fw fa-dollar likely-promoted-icon"></i>${strings.likely_promoted_yes}`;
                    } else {
                        return `<i class="fa fa-fw fa-ban"></i>${strings.likely_promoted_no}`;
                    }
                },
                csvFormat: function (data) {
                    const likelyhood = (_.isArray(data) ? _.last(data) : data);
                    if (!_.isFinite(likelyhood)) {
                        return strings.likely_promoted_unknown;
                    } else if (likelyhood >= likelyPromoted.threshold) {
                        return strings.likely_promoted_yes;
                    } else {
                        return strings.likely_promoted_no;
                    }
                }
            },
            overview: {
                description: strings.facebook_overview_description
            },
            post_video_retention_graph: {
            },
            video_time_millis: {
                format: formatter.formatTimeInterval
            },
            pct_viewers: {
                format: formatter.absolutePercent,
                name: strings.facebook_insights_pct_viewers_name
            },
            num_views_in_bucket: {
                axisFormat: function (value) {
                    return formatter.formatAbbreviated(value, formatter.formats.integer);
                }
            },
            percentInBucket: {
                format: function (value) {
                    return formatter.absolutePercent(value, {
                        decimals: 0
                    });
                }
            },
            likely_promoted: {
                description: strings.facebook_likely_promoted_posts_description
            },
            admin_creator_name: {
                name: 'Posted By',
                collection: metricEnums.collection.GENERAL,
                type: metricEnums.dataType.STRING,
                description: strings.facebook_insights_post_admin_creator_name_description
            },
            boosted_post_total_spend: {
                name: 'Spend',
                collection: metricEnums.collection.GENERAL,
                type: metricEnums.dataType.NUMERIC,
                description: strings.facebook_insights_post_boosted_post_total_spend_description
            },
            reach: {
                name: 'Reach',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_reach_description,
                summable: false
            },
            organic_reach: {
                name: 'Reach, Organic',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_organic_reach_description,
                summable: false
            },
            paid_reach: {
                name: 'Reach, Paid',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_paid_reach_description,
                summable: false
            },
            viral_reach: {
                name: 'Reach, Organic, Viral',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_viral_reach_description,
                summable: false
            },
            nonviral_reach: {
                name: 'Reach, Organic, Non-Viral',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_nonviral_reach_description,
                summable: false
            },
            post_fan_reach: {
                name: 'Fan Reach',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_fan_reach_description,
                summable: false
            },
            post_fan_reach_rate: {
                name: 'Fan Reach Rate',
                collection: metricEnums.collection.REACH,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_post_fan_reach_rate_description
            },
            post_engaged_users: {
                name: 'Engaged Users',
                collection: metricEnums.collection.ENGAGEMENT,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_engaged_users_description
            },
            post_engaged_fan: {
                name: 'Engaged Page Fans',
                collection: metricEnums.collection.ENGAGEMENT,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_engaged_fan_description
            },
            engaged_users_other: {
                name: 'Engaged Non-Page Fans',
                collection: metricEnums.collection.ENGAGEMENT,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_engaged_users_other_description
            },
            organic_engagement_total: {
                name: 'Social Engagement Total, Organic',
                collection: metricEnums.collection.ENGAGEMENT,
                type: metricEnums.dataType.INTEGER,
                description: valueByDimension('post', strings.facebook_insights_post_organic_engagement_total_description, strings.facebook_insights_organic_engagement_total_description)
            },
            paid_engagement_total: {
                name: 'Social Engagement Total, Paid',
                collection: metricEnums.collection.ENGAGEMENT,
                type: metricEnums.dataType.INTEGER,
                description: valueByDimension('post', strings.facebook_insights_post_paid_engagement_total_description, strings.facebook_insights_paid_engagement_total_description)
            },
            comments: {
                name: 'Comments',
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_comments_description;
                        } else {
                            return strings.facebook_insights_posts_comments_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_comments_description;
                        } else {
                            return strings.facebook_posts_comments_description;
                        }
                    }
                }
            },
            shares: {
                name: 'Shares',
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_shares_description;
                        } else {
                            return strings.facebook_insights_posts_shares_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_shares_description;
                        } else {
                            return strings.facebook_posts_shares_description;
                        }
                    }
                }
            },
            photo_views: {
                name: 'Photo Views, via Insights',
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_photo_views_description
            },
            video_plays: {
                name: 'Video Plays, via Insights',
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_video_plays_description
            },
            video_count: {
                name: 'Videos',
                description: strings.facebook_insights_posts_video_count_description,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                type: metricEnums.dataType.INTEGER
            },
            reel_count: {
                name: 'Reels',
                description: strings.facebook_insights_posts_reel_count_description,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                type: metricEnums.dataType.INTEGER
            },
            reel_and_video_count: {
                name: 'Reels and Videos',
                description: strings.facebook_insights_posts_reel_and_video_count_description,
                collection: metricEnums.collection.REELS_AND_VIDEOS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                type: metricEnums.dataType.INTEGER
            },
            live_audience_count: {
                name: 'Live Video Viewers',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_live_audience_count_description
            },
            post_video_length: {
                name: valueByDimension('post', 'Video Length, Reels and Videos', 'Video Length'),
                collection: valueByDimension('post', metricEnums.collection.REELS_AND_VIDEOS, metricEnums.collection.VIDEO_VIEWS_DETAILS),
                type: metricEnums.dataType.DURATION_MILLIS,
                description: strings.facebook_insights_post_post_video_length_description
            },
            post_reel_and_video_length: {
                name: 'Video Length, Reels and Videos',
                collection: metricEnums.collection.REELS_AND_VIDEOS,
                type: metricEnums.dataType.DURATION_MILLIS,
                description: strings.facebook_insights_post_post_video_length_description
            },
            post_video_avg_time_watched: {
                name: valueByDimension('post', 'Average Watch Length, Reels and Videos', 'Average Watch Length, Video'),
                collection: valueByDimension('post', metricEnums.collection.REELS_AND_VIDEOS, metricEnums.collection.VIDEO_VIEWS_DETAILS),
                type: metricEnums.dataType.DURATION_MILLIS,
                description: strings.facebook_insights_post_post_video_avg_time_watched_description
            },
            post_reel_and_video_avg_time_watched: {
                name: 'Average Watch Length, Reels and Videos',
                collection: metricEnums.collection.REELS_AND_VIDEOS,
                type: metricEnums.dataType.DURATION_MILLIS,
                description: strings.facebook_insights_post_post_video_avg_time_watched_description
            },
            avg_watch_length_pct_viewed: {
                name: 'Average Percent Viewed, Video',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.NUMERIC,
                description: strings.facebook_insights_post_avg_watch_length_pct_viewed_description
            },
            video_view_rate: {
                name: 'Video View Rate by Impression',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_video_view_rate_description
            },
            post_video_views_organic: {
                name: 'Video Views, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_views_organic_description
            },
            post_video_views_paid: {
                name: 'Video Views, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_views_paid_description
            },
            post_video_complete_views_30s: {
                name: '30-second Video Views',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_complete_views_30s_description
            },
            post_video_complete_views_30s_organic: {
                name: '30-second Video Views, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_complete_views_30s_organic_description
            },
            post_video_complete_views_30s_paid: {
                name: '30-second Video Views, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_complete_views_30s_paid_description
            },
            post_video_views_10s: {
                name: '10-second Video Views',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_views_10s_description
            },
            post_video_views_10s_organic: {
                name: '10-second Video Views, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_views_10s_organic_description
            },
            post_video_views_10s_paid: {
                name: '10-second Video Views, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_views_10s_paid_description
            },
            views_to_95_pct: {
                name: 'Video Views to 95%',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_views_to_95_pct_description
            },
            post_video_complete_views_organic: {
                name: 'Video Views to 95%, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_complete_views_organic_description
            },
            post_video_complete_views_paid: {
                name: 'Video Views to 95%, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_post_video_complete_views_paid_description
            },
            '30_second_view_rate': {
                name: '30-second Video View Rate',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_30_second_view_rate_description
            },
            organic_30_second_view_rate: {
                name: '30-second Video View Rate, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_organic_30_second_view_rate_description
            },
            paid_30_second_view_rate: {
                name: '30-second Video View Rate, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_paid_30_second_view_rate_description
            },
            '95_pct_view_rate': {
                name: '95% Video View Rate',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_95_pct_view_rate_description
            },
            '95_pct_view_rate_organic': {
                name: '95% Video View Rate, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_95_pct_view_rate_organic_description
            },
            '95_pct_view_rate_paid': {
                name: '95% Video View Rate, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                description: strings.facebook_insights_post_95_pct_view_rate_paid_description
            },
            post_video_complete_views: {
                name: 'Video Views to 95%',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_post_post_video_complete_views_description
            },
            organic_post_video_complete_views: {
                name: 'Video Views to 95%, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_organic_post_video_complete_views_description
            },
            paid_post_video_complete_views: {
                name: 'Video Views to 95%, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_paid_post_video_complete_views_description
            },
            post_video_complete_views_per_post: {
                name: 'Video Views to 95% / Video',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.NUMERIC,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_post_video_complete_views_per_post_description
            },
            organic_post_video_complete_views_per_post: {
                name: 'Video Views to 95% / Video, Organic',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.NUMERIC,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_organic_post_video_complete_views_per_post_description
            },
            paid_post_video_complete_views_per_post: {
                name: 'Video Views to 95% / Video, Paid',
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                type: metricEnums.dataType.NUMERIC,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                description: strings.facebook_insights_paid_post_video_complete_views_per_post_description
            },
            social_engagement_rate_by_reach_paid: {
                name: 'Social Engagement Rate by Reach, Paid',
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_post_social_engagement_rate_by_reach_paid_description
            },
            social_engagement_rate_by_reach_organic: {
                name: 'Social Engagement Rate by Reach, Organic',
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_post_social_engagement_rate_by_reach_organic_description
            },
            insights_engagement_rate: {
                name: 'Facebook Engagement Rate',
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_post_insights_engagement_rate_description
            },
            page_views_total: {
                name: 'Page Views',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_insights_page_views_total_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_views_total: {
                name: 'Page Views / Day',
                description: strings.facebook_insights_insights_average_daily_page_views_total_description,
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_total_actions: {
                name: 'Page CTA Clicks',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_insights_page_total_actions_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_total_actions: {
                name: 'Page CTA Clicks / Day',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_insights_average_daily_page_total_actions_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_engaged_users: { // used for totals in calculating average_daily_page_engaged_users
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_engaged_users: {
                name: 'Presence Engaged Users / Day',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_page_engaged_users_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_negative_feedback: {
                name: 'Presence Negative Feedback',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_page_negative_feedback_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_negative_feedback_unique: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_negative_feedback_unique: {
                name: 'Presence Negative Feedback / Day, Unique',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.ENGAGEMENT,
                description: strings.facebook_insights_page_negative_feedback_unique_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_unique: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_unique: {
                name: 'Presence Post Reach / Day',
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                description: strings.facebook_insights_page_posts_impressions_unique_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_paid_unique: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_paid_unique: {
                name: 'Presence Post Reach / Day, Paid',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.REACH,
                description: strings.facebook_insights_page_posts_impressions_paid_unique_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_organic_unique: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_organic_unique: {
                name: 'Presence Post Reach / Day, Organic',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.REACH,
                description: strings.facebook_insights_page_posts_impressions_organic_unique_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_viral_unique: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_viral_unique: {
                name: 'Presence Post Reach / Day, Organic, Viral',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.REACH,
                description: strings.facebook_insights_page_posts_impressions_viral_unique_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_nonviral_unique: {
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_nonviral_unique: {
                name: 'Presence Post Reach / Day, Organic, Non-Viral',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.REACH,
                description: strings.facebook_insights_page_posts_impressions_nonviral_unique_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions: {
                name: 'Presence Post Impressions',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_page_posts_impressions_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions: {
                name: 'Presence Post Impressions / Day',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_average_daily_page_posts_impressions_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_paid: {
                name: 'Presence Post Impressions, Paid',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_page_posts_impressions_paid_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_paid: {
                name: 'Presence Post Impressions / Day, Paid',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_average_daily_page_posts_impressions_paid_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_organic: {
                name: 'Presence Post Impressions, Organic',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_page_posts_impressions_organic_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_organic: {
                name: 'Presence Post Impressions / Day, Organic',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_average_daily_page_posts_impressions_organic_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_viral: {
                name: 'Presence Post Impressions, Organic, Viral',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_page_posts_impressions_viral_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_posts_impressions_nonviral: {
                name: 'Presence Post Impressions, Organic, Non-Viral',
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_page_posts_impressions_nonviral_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_viral: {
                name: 'Presence Post Impressions / Day, Organic, Viral',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_average_daily_page_posts_impressions_viral_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            average_daily_page_posts_impressions_nonviral: {
                name: 'Presence Post Impressions / Day, Organic, Non-Viral',
                type: metricEnums.dataType.NUMERIC,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.IMPRESSIONS,
                description: strings.facebook_insights_average_daily_page_posts_impressions_nonviral_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            count_insights: {
                name: 'Posts',
                description: strings.facebook_insights_posts_count_description,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ACTIVITY
            },
            posts_per_day_insights: {
                name: 'Posts / Day',
                description: strings.facebook_insights_posts_posts_per_day_description,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMERIC,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ACTIVITY
            },
            posts_per_week_insights: {
                name: 'Posts / Week',
                description: strings.facebook_insights_posts_posts_per_week_description,
                type: metricEnums.dataType.NUMERIC,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ACTIVITY,
                format: function (val) {
                    if (_.isArray(val)) {
                        val = _.last(val);
                    }
                    return formatter.formats.fixed(val);
                }
            },

            count: {
                name: 'Posts',
                description: strings.facebook_posts_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ACTIVITY
            },
            posts_per_day: {
                name: 'Posts / Day',
                description: strings.facebook_insights_posts_posts_per_day_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ACTIVITY,
                summable: false
            },
            posts_per_week: {
                name: 'Posts / Week',
                description: strings.facebook_insights_posts_posts_per_week_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ACTIVITY,
                summable: false
            },
            reach_per_post: {
                name: 'Reach / Post',
                description: strings.facebook_insights_posts_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_reach_per_post: {
                name: 'Reach / Post, Paid',
                description: strings.facebook_insights_posts_paid_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            organic_reach_per_post: {
                name: 'Reach / Post, Organic',
                description: strings.facebook_insights_posts_organic_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            viral_reach_per_post: {
                name: 'Reach / Post, Organic, Viral',
                description: strings.facebook_insights_posts_viral_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            nonviral_reach_per_post: {
                name: 'Reach / Post, Organic, Non-Viral',
                description: strings.facebook_insights_posts_nonviral_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            reach_per_reel: {
                name: 'Reach / Reel',
                description: strings.facebook_insights_posts_reach_per_reel_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            fan_reach_per_post: {
                name: 'Fan Reach / Post',
                description: strings.facebook_insights_posts_fan_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            fan_reach_rate_per_post: {
                name: 'Reach Rate by Page Fan',
                description: strings.facebook_insights_posts_fan_reach_rate_per_post_description,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            impressions_per_post: {
                name: 'Impressions / Post',
                description: strings.facebook_insights_posts_impressions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_impressions_per_post: {
                name: 'Impressions / Post, Paid',
                description: strings.facebook_insights_posts_paid_impressions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            organic_impressions_per_post: {
                name: 'Impressions / Post, Organic',
                description: strings.facebook_insights_posts_organic_impressions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            viral_impressions_per_post: {
                name: 'Impressions / Post, Organic, Viral',
                description: withDeprecatedAfterJune2026Note(strings.facebook_insights_posts_viral_impressions_per_post_description),
                deprecationReason: DEPRECATED_AFTER_JUNE_2026_REASON,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            nonviral_impressions_per_post: {
                name: 'Impressions / Post, Organic, Non-Viral',
                description: withDeprecatedAfterJune2026Note(strings.facebook_insights_posts_nonviral_impressions_per_post_description),
                deprecationReason: DEPRECATED_AFTER_JUNE_2026_REASON,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            impressions: {
                // impressions is used in two places hashtag table and custom dashboard.
                // In hashtag table this metric needs to be interpreted as "Post Impressions"
                // In custom dashboard this metric needs to be interpreted as "Presence Impressions"
                // we are checking whether metric contains analysisProperty or not because we also want
                // Post Impressions when "analysisProperty" === "mediaType"
                name: function (val, metric) {
                    if (_.includes(metric.dimensions, 'post')) {
                        return 'Impressions';
                    } else if (_.has(metric, 'analysisProperty')) {
                        return 'Post Impressions';
                    } else {
                        return 'Presence Impressions';
                    }
                },
                metricType: metricEnums.metricType.VOLUME,
                description: function (val, metric) {
                    if (_.includes(metric.dimensions, 'post')) {
                        return strings.facebook_insights_post_impressions_description;
                    } else if (_.has(metric, 'analysisProperty')) {
                        return strings.facebook_insights_posts_impressions_description;
                    } else {
                        return strings.facebook_insights_glance_impressions_description;
                    }
                },
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS
            },
            average_daily_page_impressions: {
                name: 'Presence Impressions / Day',
                metricType: metricEnums.metricType.VOLUME,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                description: strings.facebook_insights_presence_impressions_per_day_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                summable: false
            },
            impressions_insights: {
                // This is Post Impressions for use in custom dashboards
                name: 'Post Impressions',
                metricType: metricEnums.metricType.VOLUME,
                description: strings.facebook_insights_posts_impressions_description,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS
            },
            paid_impressions: {
                name: valueByDimension('post', 'Impressions, Paid', 'Post Impressions, Paid'),
                description: valueByDimension('post', strings.facebook_insights_paid_impressions_description, strings.facebook_insights_posts_paid_impressions_description),
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.IMPRESSIONS
            },
            organic_impressions: {
                name: valueByDimension('post', 'Impressions, Organic', 'Post Impressions, Organic'),
                description: valueByDimension('post', strings.facebook_insights_organic_impressions_description, strings.facebook_insights_posts_organic_impressions_description),
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.IMPRESSIONS
            },
            viral_impressions: {
                name: valueByDimension('post', 'Impressions, Organic, Viral', 'Post Impressions, Organic, Viral'),
                description: withDeprecatedAfterJune2026Note(
                    valueByDimension('post', strings.facebook_insights_viral_impressions_description, strings.facebook_insights_posts_viral_impressions_description)
                ),
                deprecationReason: DEPRECATED_AFTER_JUNE_2026_REASON,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.IMPRESSIONS
            },
            nonviral_impressions: {
                name: valueByDimension('post', 'Impressions, Organic, Non-Viral', 'Post Impressions, Organic, Non-Viral'),
                description: withDeprecatedAfterJune2026Note(
                    valueByDimension('post', strings.facebook_insights_nonviral_impressions_description, strings.facebook_insights_posts_nonviral_impressions_description)
                ),
                deprecationReason: DEPRECATED_AFTER_JUNE_2026_REASON,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.IMPRESSIONS
            },
            engaged_users_per_post: {
                name: 'Engaged Users / Post',
                description: strings.facebook_insights_posts_engaged_users_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            engaged_fans_per_post: {
                name: 'Engaged Page Fans / Post',
                description: strings.facebook_insights_posts_engaged_fans_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            engaged_others_per_post: {
                name: 'Engaged Non-Page Fans / Post',
                description: strings.facebook_insights_posts_engaged_others_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            total_post_consumptions: {
                name: 'Post Consumptions, via Insights',
                description: strings.facebook_insights_posts_total_post_consumptions_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_consumptions: {
                name: 'Consumptions, via Insights',
                description: strings.facebook_insights_post_post_consumptions_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_post_consumptions: {
                name: valueByDimension('post', 'Consumptions, via Ads', 'Post Consumptions, via Ads'),
                description: valueByDimension('post', strings.facebook_insights_post_paid_post_consumptions_description, strings.facebook_insights_posts_paid_post_consumptions_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_consumptions_per_post: {
                name: 'Consumptions / Post, via Insights',
                description: strings.facebook_insights_posts_post_consumptions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_post_consumptions_per_post: {
                name: 'Consumptions / Post, via Ads',
                description: strings.facebook_insights_posts_paid_post_consumptions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            total_link_clicks: {
                name: 'Link clicks, via Insights',
                description: strings.facebook_insights_posts_total_link_clicks_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            link_clicks: {
                name: 'Link clicks, via Insights',
                description: strings.facebook_insights_post_link_clicks_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_link_clicks: {
                name: 'Link Clicks, via Ads',
                description: valueByDimension('post', strings.facebook_insights_post_paid_link_clicks_description, strings.facebook_insights_posts_paid_link_clicks_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            link_clicks_per_post: {
                name: 'Link Clicks / Post, via Insights',
                description: strings.facebook_insights_posts_link_clicks_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_link_clicks_per_post: {
                name: 'Link Clicks / Post, via Ads',
                description: strings.facebook_insights_posts_paid_link_clicks_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            total_photo_views: {
                name: 'Photo Views',
                description: strings.facebook_insights_posts_total_photo_views_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_photo_views: {
                name: 'Photo Views, via Ads',
                description: valueByDimension('post', strings.facebook_insights_post_paid_photo_views_description, strings.facebook_insights_posts_paid_photo_views_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            photo_views_per_post: {
                name: 'Photo Views / Post, via Insights',
                description: strings.facebook_insights_posts_photo_views_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_photo_views_per_post: {
                name: 'Photo Views / Post, via Ads',
                description: strings.facebook_insights_posts_paid_photo_views_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            total_video_plays: {
                name: 'Video Plays, via Insights',
                description: strings.facebook_insights_posts_total_video_plays_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_video_plays: {
                name: 'Video Plays, via Ads',
                description: valueByDimension('post', strings.facebook_insights_post_paid_video_plays_description, strings.facebook_insights_posts_paid_video_plays_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            video_plays_per_post: {
                name: 'Video Plays / Post, via Insights',
                description: strings.facebook_insights_posts_video_plays_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_video_plays_per_post: {
                name: 'Video Plays / Post, via Ads',
                description: strings.facebook_insights_posts_paid_video_plays_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            total_other_clicks: {
                name: 'Other Clicks, via Insights',
                description: strings.facebook_insights_posts_total_other_clicks_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            other_clicks: {
                name: 'Other Clicks, via Insights',
                description: strings.facebook_insights_post_other_clicks_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_other_clicks: {
                name: 'Other Clicks, via Ads',
                description: valueByDimension('post', strings.facebook_insights_post_paid_other_clicks_description, strings.facebook_insights_posts_paid_other_clicks_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            other_clicks_per_post: {
                name: 'Other Clicks / Post, via Insights',
                description: strings.facebook_insights_posts_other_clicks_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_other_clicks_per_post: {
                name: 'Other Clicks / Post, via Ads',
                description: strings.facebook_insights_posts_paid_other_clicks_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            social_engagement_total: {
                name: 'Social Engagement Total',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_total_description, strings.facebook_insights_posts_social_engagement_total_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_social_engagement_total: {
                name: 'Social Engagement Total, Paid',
                description: strings.facebook_insights_posts_paid_social_engagement_total_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_social_engagement_total: {
                name: 'Social Engagement Total, Organic',
                description: strings.facebook_insights_posts_organic_social_engagement_total_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_social_engagement_total: {
                name: 'Social Engagement Total, Post-only',
                description: valueByDimension('post', strings.facebook_insights_post_post_only_social_engagement_total_description, strings.facebook_insights_posts_post_only_social_engagement_total_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            social_engagement_total_per_post: {
                name: 'Social Engagement Total / Post',
                description: strings.facebook_insights_posts_social_engagement_total_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_social_engagement_total_per_post: {
                name: 'Social Engagement Total / Post, Paid',
                description: strings.facebook_insights_posts_paid_social_engagement_total_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            organic_social_engagement_total_per_post: {
                name: 'Social Engagement Total / Post, Organic',
                description: strings.facebook_insights_posts_organic_social_engagement_total_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            post_only_social_engagement_total_per_post: {
                name: 'Social Engagement Total / Post, Post-only',
                description: strings.facebook_insights_posts_post_only_social_engagement_total_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            engagement_total_insights: {
                name: 'Engagement Total',
                description: strings.facebook_insights_posts_engagement_total_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            engagement_total_per_post_insights: {
                name: 'Engagement Total / Post',
                description: strings.facebook_insights_posts_engagement_total_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            engagement_total: {
                name: 'Engagement Total',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_engagement_total_description;
                        } else {
                            return strings.facebook_insights_posts_engagement_total_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_engagement_total_description;
                        } else {
                            return strings.facebook_posts_engagement_total_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT
            },
            engagement_total_per_post: {
                name: 'Engagement Total / Post',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        return strings.facebook_insights_posts_engagement_total_per_post_description;
                    }
                    return strings.facebook_engagement_total_per_post_description;
                },
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                summable: false
            },
            facebook_engagement_rate: {
                name: 'Facebook Engagement Rate',
                description: strings.facebook_insights_posts_facebook_engagement_rate_description,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            social_engagement_rate_by_reach: {
                name: 'Social Engagement Rate by Reach',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_rate_by_reach_description, strings.facebook_insights_posts_social_engagement_rate_by_reach_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_social_engagement_rate_by_reach: {
                name: 'Social Engagement Rate by Reach, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_rate_by_reach_paid_description, strings.facebook_insights_posts_paid_social_engagement_rate_by_reach_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_social_engagement_rate_by_reach: {
                name: 'Social Engagement Rate by Reach, Organic',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_rate_by_reach_organic_description, strings.facebook_insights_posts_organic_social_engagement_rate_by_reach_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            social_engagement_rate_by_impression: {
                name: 'Social Engagement Rate by Impression',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_rate_by_impression_description, strings.facebook_insights_posts_social_engagement_rate_by_impression_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_social_engagement_rate_by_impression: {
                name: 'Social Engagement Rate by Impression, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_rate_by_impression_paid_description, strings.facebook_insights_posts_paid_social_engagement_rate_by_impression_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_social_engagement_rate_by_impression: {
                name: 'Social Engagement Rate by Impression, Organic',
                description: valueByDimension('post', strings.facebook_insights_post_social_engagement_rate_by_impression_organic_description, strings.facebook_insights_posts_organic_social_engagement_rate_by_impression_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            engagement_rate_by_impression: {
                name: 'Engagement Rate by Impression',
                description: valueByDimension('post', strings.facebook_insights_post_engagement_rate_by_impression_description, strings.facebook_insights_posts_engagement_rate_by_impression_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            engagement_rate_by_reach: {
                name: 'Engagement Rate by Reach',
                description: valueByDimension('post', strings.facebook_insights_post_engagement_rate_by_reach_description, strings.facebook_insights_posts_engagement_rate_by_reach_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            engagement_rate_by_presence_reach: {
                name: 'Engagement Rate by Page Fan',
                description: valueByDimension('post', strings.facebook_insights_engagement_rate_by_presence_reach_description, strings.facebook_insights_posts_engagement_rate_by_presence_reach_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            social_engagement_rate_by_presence_reach: {
                name: 'Social Engagement Rate by Page Fan',
                description: valueByDimension('post', strings.facebook_insights_social_engagement_rate_by_presence_reach_description, strings.facebook_insights_posts_social_engagement_rate_by_presence_reach_description),
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            likes: {
                name: 'Likes',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_likes_description;
                        } else {
                            return strings.facebook_insights_posts_likes_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_likes_description;
                        } else {
                            return strings.facebook_posts_likes_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_likes: {
                name: 'Likes, Post-only',
                description: strings.facebook_insights_post_post_only_likes_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            likes_per_post: {
                name: 'Likes / Post',
                description: strings.facebook_insights_posts_likes_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            loves: {
                name: 'Loves',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_loves_description;
                        } else {
                            return strings.facebook_insights_posts_loves_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_loves_description;
                        } else {
                            return strings.facebook_posts_loves_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_loves: {
                name: 'Loves, Post-only',
                description: strings.facebook_insights_post_post_only_loves_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            loves_per_post: {
                name: 'Loves / Post',
                description: strings.facebook_insights_posts_loves_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            hahas: {
                name: 'Hahas',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_hahas_description;
                        } else {
                            return strings.facebook_insights_posts_hahas_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_hahas_description;
                        } else {
                            return strings.facebook_posts_hahas_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_hahas: {
                name: 'Hahas, Post-only',
                description: strings.facebook_insights_post_post_only_hahas_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            hahas_per_post: {
                name: 'Hahas / Post',
                description: strings.facebook_insights_posts_hahas_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            wows: {
                name: 'Wows',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_wows_description;
                        } else {
                            return strings.facebook_insights_posts_wows_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_wows_description;
                        } else {
                            return strings.facebook_posts_wows_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_wows: {
                name: 'Wows, Post-only',
                description: strings.facebook_insights_post_post_only_wows_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            wows_per_post: {
                name: 'Wows / Post',
                description: strings.facebook_insights_posts_wows_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            sads: {
                name: 'Sads',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_sads_description;
                        } else {
                            return strings.facebook_insights_posts_sads_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_sads_description;
                        } else {
                            return strings.facebook_posts_sads_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_sads: {
                name: 'Sads, Post-only',
                description: strings.facebook_insights_post_post_only_sads_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            sads_per_post: {
                name: 'Sads / Post',
                description: strings.facebook_insights_posts_sads_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            angrys: {
                name: 'Angrys',
                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_angrys_description;
                        } else {
                            return strings.facebook_insights_posts_angrys_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_angrys_description;
                        } else {
                            return strings.facebook_posts_angrys_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_angrys: {
                name: 'Angrys, Post-only',
                description: strings.facebook_insights_post_post_only_angrys_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            angrys_per_post: {
                name: 'Angrys / Post',
                description: strings.facebook_insights_posts_angrys_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            negative_feedback: {
                name: 'Negative Feedback',
                description: valueByDimension('post', strings.facebook_insights_post_negative_feedback_description, strings.facebook_insights_posts_negative_feedback_totals_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            negative_feedback_per_post: {
                name: 'Negative Feedback / Post',
                description: strings.facebook_insights_posts_negative_feedback_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            negative_feedback_unique: {
                name: 'Negative Feedback, Unique',
                description: strings.facebook_insights_post_negative_feedback_unique_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            negative_feedback_unique_per_post: {
                name: 'Negative Feedback / Post, Unique',
                description: strings.facebook_insights_posts_negative_feedback_unique_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            fan_post_impressions: {
                name: 'Fan Post Impressions',
                description: valueByDimension('post', strings.facebook_insights_post_fan_post_impressions_description, strings.facebook_insights_posts_fan_post_impressions_total_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            fan_impressions_per_post: {
                name: 'Fan Impressions / Post',
                description: strings.facebook_insights_posts_fan_impressions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_fan_impressions: {
                name: 'Fan Post Impressions, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_paid_fan_impressions_description, strings.facebook_insights_posts_paid_fan_impressions_total_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_fan_impressions_per_post: {
                name: 'Fan Impressions / Post, Paid',
                description: strings.facebook_insights_posts_paid_fan_impressions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.IMPRESSIONS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            paid_fan_reach: {
                name: 'Fan Reach, Paid',
                description: strings.facebook_insights_post_paid_fan_reach_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_fan_reach_per_post: {
                name: 'Fan Reach / Post, Paid',
                description: strings.facebook_insights_posts_paid_fan_reach_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REACH,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                summable: false
            },
            reactions: {
                name: 'Reactions',
                category: 'reactions',

                description: function (val, metric) {
                    if (metric.source === metricEnums.sources.FACEBOOK_INSIGHTS) {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_insights_post_reactions_description;
                        } else {
                            return strings.facebook_insights_posts_reactions_description;
                        }
                    } else {
                        if (_.includes(metric.dimensions, 'post')) {
                            return strings.facebook_post_reactions_description;
                        } else {
                            return strings.facebook_posts_reactions_description;
                        }
                    }
                },
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            total_reactions: {
                name: 'Total Reactions',
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                type: metricEnums.dataType.INTEGER,
                description: strings.facebook_insights_post_story_reactions
            },
            paid_reactions: {
                name: 'Reactions, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_paid_reactions_description, strings.facebook_insights_posts_paid_reactions_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_reactions: {
                name: 'Reactions, Organic',
                description: valueByDimension('post', strings.facebook_insights_post_organic_reactions_description, strings.facebook_insights_posts_organic_reactions_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_reactions: {
                name: 'Reactions, Post-only',
                description: valueByDimension('post', strings.facebook_insights_post_post_only_reactions_description, strings.facebook_insights_posts_post_only_reactions_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            reactions_per_post: {
                name: 'Reactions / Post',
                description: strings.facebook_insights_posts_reactions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_reactions_per_post: {
                name: 'Reactions / Post, Paid',
                description: strings.facebook_insights_posts_paid_reactions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_reactions_per_post: {
                name: 'Reactions / Post, Organic',
                description: strings.facebook_insights_posts_organic_reactions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_reactions_per_post: {
                name: 'Reactions / Post, Post-only',
                description: strings.facebook_insights_posts_post_only_reactions_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            total_comments: {
                name: 'Comments',
                description: strings.facebook_insights_posts_total_comments_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_comments: {
                name: 'Comments, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_paid_comments_description, strings.facebook_insights_posts_paid_comments_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_comments: {
                name: 'Comments, Organic',
                description: valueByDimension('post', strings.facebook_insights_post_organic_comments_description, strings.facebook_insights_posts_organic_comments_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_comments: {
                name: 'Comments, Post-only',
                description: valueByDimension('post', strings.facebook_insights_post_post_only_comments_description, strings.facebook_insights_posts_post_only_comments_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            comments_per_post: {
                name: 'Comments / Post',
                description: strings.facebook_insights_posts_comments_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_comments_per_post: {
                name: 'Comments / Post, Paid',
                description: strings.facebook_insights_posts_paid_comments_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_comments_per_post: {
                name: 'Comments / Post, Organic',
                description: strings.facebook_insights_posts_organic_comments_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_comments_per_post: {
                name: 'Comments / Post, Post-only',
                description: strings.facebook_insights_posts_post_only_comments_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            total_shares: {
                name: 'Shares',
                description: valueByDimension('post', strings.facebook_insights_post_shares_description, strings.facebook_insights_posts_total_shares_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_shares: {
                name: 'Shares, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_paid_shares_description, strings.facebook_insights_posts_paid_shares_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_shares: {
                name: 'Shares, Organic',
                description: valueByDimension('post', strings.facebook_insights_post_organic_shares_description, strings.facebook_insights_posts_organic_shares_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_shares: {
                name: 'Shares, Post-only',
                description: valueByDimension('post', strings.facebook_insights_post_post_only_shares_description, strings.facebook_insights_posts_post_only_shares_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            shares_per_post: {
                name: 'Shares / Post',
                description: strings.facebook_insights_posts_shares_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_shares_per_post: {
                name: 'Shares / Post, Paid',
                description: strings.facebook_insights_posts_paid_shares_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_shares_per_post: {
                name: 'Shares / Post, Organic',
                description: strings.facebook_insights_posts_organic_shares_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_only_shares_per_post: {
                name: 'Shares / Post, Post-only',
                description: strings.facebook_insights_posts_post_only_shares_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            live_audience_count_per_post: {
                name: 'Live Video Viewers / Video',
                description: strings.facebook_insights_posts_live_audience_count_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_video_length_per_post: {
                name: 'Video Length / Video',
                description: strings.facebook_insights_posts_post_video_length_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_video_avg_time_watched_per_post: {
                name: 'Average Watch Length / Video',
                description: strings.facebook_insights_posts_post_video_avg_time_watched_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_reel_avg_time_watched_per_post: {
                name: 'Average Watch Length / Reel',
                description: strings.facebook_insights_posts_post_reel_avg_time_watched_per_reel_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_reel_and_video_avg_time_watched_per_post: {
                name: 'Average Watch Length, Reels and Videos',
                description: strings.facebook_insights_posts_post_reel_and_video_avg_time_watched_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.REELS_AND_VIDEOS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            avg_watch_length_pct_viewed_per_post: {
                name: 'Average Percent Viewed / Video',
                description: strings.facebook_insights_posts_avg_watch_length_pct_viewed_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            video_view_rate_per_post: {
                name: 'Video View Rate by Impression',
                description: strings.facebook_insights_posts_video_view_rate_per_post_description,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_video_views: {
                name: valueByDimension('post', 'Views, Reels and Videos', 'Video Views'),
                description: valueByDimension('post', strings.facebook_insights_post_post_video_views_description, strings.facebook_insights_posts_post_video_views_description),
                type: metricEnums.dataType.INTEGER,
                collection: valueByDimension('post', metricEnums.collection.REELS_AND_VIDEOS, metricEnums.collection.VIDEO_VIEWS_DETAILS),
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_reel_and_video_views: {
                name: 'Views, Reels and Videos',
                description: valueByDimension('post', strings.facebook_insights_post_post_reel_and_video_views_description, strings.facebook_insights_posts_post_reel_and_video_views_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.REELS_AND_VIDEOS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_reel_views: {
                name: 'Reel Views',
                description: valueByDimension('post', strings.facebook_insights_post_post_reel_views_description, strings.facebook_insights_posts_post_reel_views_description),
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_post_video_views: {
                name: 'Video Views, Organic',
                description: strings.facebook_insights_posts_organic_post_video_views_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_post_video_views: {
                name: 'Video Views, Paid',
                description: strings.facebook_insights_posts_paid_post_video_views_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_video_views_per_post: {
                name: 'Video Views / Video',
                description: strings.facebook_insights_posts_post_video_views_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_post_video_views_per_post: {
                name: 'Video Views / Video, Organic',
                description: strings.facebook_insights_posts_organic_post_video_views_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_post_video_views_per_post: {
                name: 'Video Views / Video, Paid',
                description: strings.facebook_insights_posts_paid_post_video_views_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            post_reel_views_per_post: {
                name: 'Reel Views / Reel',
                description: strings.facebook_insights_posts_post_reel_views_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views: {
                name: 'Presence Video Views',
                description: strings.facebook_insights_page_video_views_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_organic: {
                name: 'Presence Video Views, Organic',
                description: strings.facebook_insights_page_video_views_organic_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_paid: {
                name: 'Presence Video Views, Paid',
                description: strings.facebook_insights_page_video_views_paid_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_per_day: {
                name: 'Presence Video Views / Day',
                description: strings.facebook_insights_page_video_views_per_day_description,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMBERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_organic_per_day: {
                name: 'Presence Video Views / Day, Organic',
                description: strings.facebook_insights_page_video_views_organic_per_day_description,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMBERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_paid_per_day: {
                name: 'Presence Video Views / Day, Paid',
                description: strings.facebook_insights_page_video_views_paid_per_day_description,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                type: metricEnums.dataType.NUMBERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_repeat_views: {
                name: 'Presence Video View Repeats',
                description: strings.facebook_insights_page_video_repeat_views_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_unique: {
                name: 'Presence Video Viewers / Day',
                description: strings.facebook_insights_page_video_views_unique_description,
                type: metricEnums.dataType.INTEGER,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_10s: {
                name: 'Presence 10-second Video Views',
                description: strings.facebook_insights_page_video_views_10s_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_10s_organic: {
                name: 'Presence 10-second Video Views, Organic',
                description: strings.facebook_insights_page_video_views_10s_organic_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_10s_paid: {
                name: 'Presence 10-second Video Views, Paid',
                description: strings.facebook_insights_page_video_views_10s_paid_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_10s_repeat: {
                name: 'Presence 10-second Video Views Repeats',
                description: strings.facebook_insights_page_video_views_10s_repeat_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            page_video_views_10s_unique: {
                name: 'Presence 10-Second Video Viewers / Day',
                description: strings.facebook_insights_page_video_views_10s_unique_description,
                type: metricEnums.dataType.INTEGER,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            video_viewers: {
                name: 'Video Viewers',
                description: strings.facebook_insights_post_video_viewers_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            video_viewers_per_post: {
                name: 'Video Viewers / Video',
                description: strings.facebook_insights_posts_video_viewers_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_video_viewers: {
                name: 'Video Viewers, Organic',
                description: strings.facebook_insights_post_organic_video_viewers_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_video_viewers_per_post: {
                name: 'Video Viewers / Video, Organic',
                description: strings.facebook_insights_posts_organic_video_viewers_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_video_viewers: {
                name: 'Video Viewers, Paid',
                description: strings.facebook_insights_post_paid_video_viewers_description,
                type: metricEnums.dataType.INTEGER,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_video_viewers_per_post: {
                name: 'Video Viewers / Video, Paid',
                description: strings.facebook_insights_posts_paid_video_viewers_per_post_description,
                type: metricEnums.dataType.NUMERIC,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            video_view_length: {
                name: valueByDimension('post', 'Total Time Viewed, Reels and Videos', 'Video View Length'),
                description: valueByDimension('post', strings.facebook_insights_post_video_view_length_description, strings.facebook_insights_posts_video_view_length_total_description),
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: valueByDimension('post', metricEnums.collection.REELS_AND_VIDEOS, metricEnums.collection.VIDEO_VIEWS_DETAILS),
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            reel_view_length: {
                name: 'Reels Total Time Viewed',
                description: valueByDimension('post', strings.facebook_insights_post_reel_view_length_description, strings.facebook_insights_posts_reel_view_length_total_description),
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            reel_and_video_view_length: {
                name: 'Total Time Viewed, Reels and Videos',
                description: valueByDimension('post', strings.facebook_insights_post_video_view_length_description, strings.facebook_insights_posts_reel_and_video_view_length_total_description),
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.REELS_AND_VIDEOS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            video_view_length_per_post: {
                name: 'Video View Length / Video',
                description: strings.facebook_insights_posts_video_view_length_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            reel_view_length_per_post: {
                name: 'Reel View Length / Reel',
                description: strings.facebook_insights_posts_video_view_length_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.REEL_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_video_view_length: {
                name: 'Video View Length, Organic',
                description: valueByDimension('post', strings.facebook_insights_post_organic_video_view_length_description, strings.facebook_insights_posts_organic_video_view_length_total_description),
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            organic_video_view_length_per_post: {
                name: 'Video View Length / Video, Organic',
                description: strings.facebook_insights_posts_organic_video_view_length_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_video_view_length: {
                name: 'Video View Length, Paid',
                description: valueByDimension('post', strings.facebook_insights_post_paid_video_view_length_description, strings.facebook_insights_posts_paid_video_view_length_total_description),
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            paid_video_view_length_per_post: {
                name: 'Video View Length / Video, Paid',
                description: strings.facebook_insights_posts_paid_video_view_length_per_post_description,
                type: metricEnums.dataType.DURATION_MILLIS,
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS,
                source: metricEnums.sources.FACEBOOK_INSIGHTS
            },
            phone_call_clicks: {
                name: 'Phone Call Clicks',
                description: strings.facebook_insights_page_call_phone_clicks_logged_in_unique_description,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            direction_clicks: {
                name: 'Direction Clicks',
                description: strings.facebook_insights_page_get_directions_clicks_logged_in_unique_description,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            website_clicks: {
                name: 'Website Clicks',
                description: strings.facebook_insights_page_website_clicks_logged_in_unique_description,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            cta_button_clicks: {
                name: 'CTA Button Clicks',
                description: strings.facebook_insights_page_cta_clicks_logged_in_unique_description,
                type: metricEnums.dataType.INTEGER,
                source: metricEnums.sources.FACEBOOK_INSIGHTS,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },

            // We are using adObjectId as a proxy for source in some sense. If we ever build the Universal Metrics API we should be able to work
            // around measure collision given that it operates (dispatches) on source rather than channel. In other words: the Facebook
            // impressions won't collide with Facebook Ads impressions, as we will control the metadata on the server side.
            adObjectId: definitions.facebookAds
        };
    }
})();
